import React from 'react';
import * as T from '@tabler/icons-react';

// Compatibility layer for the existing UI. Every icon rendered by the app now
// comes from Tabler and shares the same 1.75 stroke, including legacy imports.
const icon = (Component) => {
  const Wrapped = React.forwardRef(({ stroke, strokeWidth, ...props }, ref) => (
    <Component ref={ref} stroke={stroke ?? strokeWidth ?? 2} {...props} />
  ));
  Wrapped.displayName = Component?.displayName || Component?.name || 'TablerIcon';
  return Wrapped;
};

export const AlertCircle = icon(T.IconAlertCircle);
export const AlertTriangle = icon(T.IconAlertTriangle);
export const Thermometer = icon(T.IconThermometer);
export const Snowflake = icon(T.IconSnowflake);
export const Umbrella = icon(T.IconUmbrella);
export const AlignCenter = icon(T.IconAlignCenter);
export const AlignLeft = icon(T.IconAlignLeft);
export const AlignRight = icon(T.IconAlignRight);
export const Apple = icon(T.IconApple);
export const ArrowDown = icon(T.IconArrowDown);
export const ArrowLeft = icon(T.IconArrowLeft);
export const ArrowRight = icon(T.IconArrowRight);
export const ArrowUp = icon(T.IconArrowUp);
export const ArrowUpDown = icon(T.IconArrowsSort);
export const Ban = icon(T.IconBan);
export const Beef = icon(T.IconMeat);
export const Beer = icon(T.IconBeer);
export const Bold = icon(T.IconBold);
export const Bluetooth = icon(T.IconBluetooth);
export const BookOpen = icon(T.IconNotebook);
export const BuildingWarehouse = icon(T.IconBuildingWarehouse);
export const Notebook = icon(T.IconNotebook);
export const Building2 = icon(T.IconBuilding);
export const Cake = icon(T.IconCake);
export const CalendarClock = icon(T.IconCalendarDue);
export const CalendarDue = icon(T.IconCalendarDue);
export const Camera = icon(T.IconCamera);
export const CameraOff = icon(T.IconCameraOff);
export const Candy = icon(T.IconCandy);
export const Carrot = icon(T.IconCarrot);
export const Check = icon(T.IconCheck);
export const CheckCircle2 = icon(T.IconCircleCheck);
export const ChefHat = icon(T.IconChefHat);
export const ChevronDown = icon(T.IconChevronDown);
export const ChevronLeft = icon(T.IconChevronLeft);
export const ChevronRight = icon(T.IconChevronRight);
export const ChevronUp = icon(T.IconChevronUp);
export const ChevronsUpDown = icon(T.IconSelector);
export const Circle = icon(T.IconCircle);
export const CircleAlert = icon(T.IconAlertCircle);
export const CircleMinus = icon(T.IconCircleMinus);
export const ClipboardList = icon(T.IconClipboardCheck);
export const Clock = icon(T.IconClock);
export const Clock3 = icon(T.IconClockExclamation);
export const Coffee = icon(T.IconCoffee);
export const Command = icon(T.IconCommand);
export const Cookie = icon(T.IconCookie);
export const Copy = icon(T.IconCopy);
export const Croissant = icon(T.IconBread);
export const CupSoda = icon(T.IconCup);
export const Download = icon(T.IconDownload);
export const Droplets = icon(T.IconDroplet);
export const Edit = icon(T.IconPencil);
export const Edit3 = icon(T.IconPencil);
export const Egg = icon(T.IconEgg);
export const Eye = icon(T.IconEye);
export const Factory = icon(T.IconChefHat);
export const FileScan = icon(T.IconScan);
export const FileText = icon(T.IconFileAnalytics);
export const Filter = icon(T.IconAdjustmentsHorizontal);
export const Flashlight = icon(T.IconBolt);
export const Fish = icon(T.IconFish);
export const Grape = icon(T.IconApple);
export const GripVertical = icon(T.IconGripVertical);
export const History = icon(T.IconClipboardCheck);
export const IceCream = icon(T.IconIceCream2);
export const Inbox = icon(T.IconInbox);
export const Key = icon(T.IconKey);
export const KeyRound = icon(T.IconKey);
export const Layers3 = icon(T.IconStack2);
export const LayoutDashboard = icon(T.IconLayoutDashboard);
export const ListDetails = icon(T.IconListDetails);
export const Leaf = icon(T.IconLeaf);
export const Loader2 = icon(T.IconLoader2);
export const Lock = icon(T.IconLock);
export const LogOut = icon(T.IconLogout2);
export const Mail = icon(T.IconMail);
export const MapPin = icon(T.IconMapPin);
export const Menu = icon(T.IconMenu2);
export const MessageCircle = icon(T.IconMessageCircle);
export const Milk = icon(T.IconMilk);
export const Minus = icon(T.IconMinus);
export const MoreHorizontal = icon(T.IconDots);
export const MoreVertical = icon(T.IconDotsVertical);
export const Network = icon(T.IconRoute);
export const Package = icon(T.IconPackage);
export const PackageImport = icon(T.IconPackageImport);
export const PackageCheck = icon(T.IconPackageExport);
export const PackageOpen = icon(T.IconPackage);
export const PackageSearch = icon(T.IconPackageImport);
export const PanelLeft = icon(T.IconLayoutSidebarLeftCollapse);
export const Pencil = icon(T.IconPencil);
export const Phone = icon(T.IconPhone);
export const Pizza = icon(T.IconPizza);
export const Plus = icon(T.IconPlus);
export const Popcorn = icon(T.IconGrain);
export const Printer = icon(T.IconPrinter);
export const QrCode = icon(T.IconQrcode);
export const RefreshCw = icon(T.IconRefresh);
export const RotateCcw = icon(T.IconRotateClockwise2);
export const Route = icon(T.IconRoute);
export const Salad = icon(T.IconSalad);
export const Sandwich = icon(T.IconBurger);
export const Save = icon(T.IconDeviceFloppy);
export const ScanLine = icon(T.IconScan);
export const Scissors = icon(T.IconScissors);
export const Search = icon(T.IconSearch);
export const Send = icon(T.IconSend2);
export const Settings = icon(T.IconSettings);
export const Shield = icon(T.IconShield);
export const ShieldCheck = icon(T.IconShieldCheck);
export const SlidersHorizontal = icon(T.IconAdjustmentsHorizontal);
export const Soup = icon(T.IconSoup);
export const Sparkles = icon(T.IconSparkles);
export const Star = icon(T.IconStar);
export const Tag = icon(T.IconTag);
export const Trash2 = icon(T.IconTrash);
export const TrendingUp = icon(T.IconTrendingUp);
export const Truck = icon(T.IconTruckDelivery);
export const Type = icon(T.IconTypography);
export const Upload = icon(T.IconUpload);
export const User = icon(T.IconUser);
export const UserRound = icon(T.IconUserCircle);
export const Users = icon(T.IconUsers);
export const Utensils = icon(T.IconToolsKitchen2);
export const UtensilsCrossed = icon(T.IconToolsKitchen2);
export const Warehouse = icon(T.IconBuildingWarehouse);
export const Wheat = icon(T.IconWheat);
export const Wifi = icon(T.IconWifi);
export const WifiOff = icon(T.IconWifiOff);
export const Wine = icon(T.IconGlass);
export const Wrench = icon(T.IconTool);
export const X = icon(T.IconX);
export const ZoomIn = icon(T.IconZoomIn);
export const ZoomOut = icon(T.IconZoomOut);
